---
title: "Late August Update"
date: "2026-08-27"
---

Our largest update so far. The Entries page has been redesigned, timers are new, and email tracking is more precise.

**If anything here is unclear, please reach out.** We would rather walk you through it than have you guess: <a href="mailto:tj@hourglass.law">tj@hourglass.law</a>, <a href="mailto:arturo@hourglass.law">arturo@hourglass.law</a>, or <a href="mailto:dayn@hourglass.law">dayn@hourglass.law</a>.

---

## <IconHeading icon="Sparkles">A Refreshed Look</IconHeading>

Hourglass has been redesigned. Nothing has been removed, but a lot has moved. The sections below cover everything that did.

---

## <IconHeading icon="Monitor">Desktop Application</IconHeading>

**Email tracking is more precise.** Time spent in an email is now captured in 5 second increments, so quick replies and short reviews are recorded rather than rounded away. You may notice more short email entries, particularly on days made up of many brief exchanges.

Plus bug fixes and performance improvements throughout the desktop app.

---

## <IconHeading icon="Table">The Entries Page</IconHeading>

Your entries live on the [Entries](/entries) page across three tabs: **Tracked** for entries Hourglass created and ones you have reviewed, **Exported** for entries already sent to billing, and **Deleted** for anything you have removed, which can be recovered from there.

**Reviewed icons.** Entries now show whether you have reviewed them, so you can spot unchecked ones before an export.

**Filters have moved** next to the date picker. Filter by status, application, matter, client, and now **billing status**, which separates billable from non billable work.

<mark>**The date picker works differently.**</mark> This is the change most likely to catch you out. Click the center to open the calendar, then:

- **Single day:** double click that date.
- **Date range:** click the first date, then the last date.

**Search is new.** The fastest way to find an entry by its narrative, without setting up filters.

---

## <IconHeading icon="Timer">Timers</IconHeading>

You can now time work directly in Hourglass, which covers what the desktop app cannot see: phone calls, meetings away from your desk, anything on paper.

**Starting a timer creates an entry immediately.** Fill in the matter, codes, and description while it runs. The date and units stay locked until you stop, since Hourglass is keeping that time for you.

**Pausing saves the time onto the entry.** To resume, hover over that row and click the timer icon on the right. It picks up where you left off. This works on any entry, not only ones a timer created, so you can add time to an entry Hourglass captured from an email or document.

> <mark>**While a timer runs, Hourglass pauses automatic tracking.**</mark> Otherwise the work you are timing would also be captured separately and counted twice. Normal tracking resumes on its own when you pause. Nothing to switch back on.

> **A running timer blocks combine and export** for that entry, since both would rewrite time still being counted. Pause first.

---

## <IconHeading icon="ArrowUpDown">Sorting, Columns, and Totals</IconHeading>

**Sort by any column.** Click a header to sort by it. Up arrow is ascending, down is descending.

<mark>**Choose and reorder columns.**</mark> The last icon on the right of the header opens the column menu. Uncheck to hide, or drag the six dot handle to reorder. Your layout is remembered.

<mark>**Total time has moved to the bottom right.**</mark> The status and billable sections both add up to the total shown there.

---

## <IconHeading icon="Calendar">Calendar Page</IconHeading>

The filter icon in the top left shows totals for different groups of hours.

> <mark>**If hours you expect are missing, check the status filter first.**</mark> A status left unchecked there is by far the most common cause.

---

## <IconHeading icon="Settings">Settings</IconHeading>

**Abbreviations increased from 100 to 500.**

<mark>**Abbreviation matching is smarter.**</mark> Case is ignored by default, so `ab` finds the shortcut you saved as `Ab`. Create two that differ only by case and Hourglass treats the casing as meaningful, matching each exactly.

---

## <IconHeading icon="Wrench">Bug Fixes & Stability</IconHeading>

Numerous bug fixes, performance improvements, and stability work across the application.
